/**
 * Deal Data Validation - per-field validators
 *
 * Each validator returns { isValid, value, reason } for consistent error handling.
 */

import type { ValidationResult } from "./deal-data-validation.types";

/**
 * Validate deal revenue
 * - Must be a non-negative number
 * - Null/undefined is invalid (not a default)
 */
export function validateDealRevenue(revenue: unknown): ValidationResult<number> {
  if (revenue === null || revenue === undefined) {
    return {
      isValid: false,
      value: 0,
      reason: "Revenue is null or undefined",
    };
  }

  const numRevenue = Number(revenue);
  if (isNaN(numRevenue)) {
    return {
      isValid: false,
      value: 0,
      reason: "Revenue is not a valid number",
    };
  }

  if (numRevenue < 0) {
    return {
      isValid: false,
      value: 0,
      reason: "Revenue cannot be negative",
    };
  }

  return {
    isValid: true,
    value: numRevenue,
  };
}

/**
 * Validate deal probability
 * - Must be between 0 and 100
 * - Null/undefined is invalid (not a default)
 */
export function validateDealProbability(
  probability: unknown
): ValidationResult<number | null> {
  if (probability === null || probability === undefined) {
    return {
      isValid: false,
      value: null,
      reason: "Probability is null or undefined",
    };
  }

  const numProbability = Number(probability);
  if (isNaN(numProbability)) {
    return {
      isValid: false,
      value: null,
      reason: "Probability is not a valid number",
    };
  }

  if (numProbability < 0 || numProbability > 100) {
    return {
      isValid: false,
      value: null,
      reason: `Probability must be between 0 and 100, got ${numProbability}`,
    };
  }

  return {
    isValid: true,
    value: numProbability,
  };
}

/**
 * Validate deal cost
 * - Must be a non-negative number
 * - Null/undefined is valid (cost is optional)
 */
export function validateDealCost(cost: unknown): ValidationResult<number | null> {
  if (cost === null || cost === undefined) {
    return {
      isValid: true,
      value: null,
    };
  }

  const numCost = Number(cost);
  if (isNaN(numCost)) {
    return {
      isValid: false,
      value: null,
      reason: "Cost is not a valid number",
    };
  }

  if (numCost < 0) {
    return {
      isValid: false,
      value: null,
      reason: "Cost cannot be negative",
    };
  }

  return {
    isValid: true,
    value: numCost,
  };
}

/**
 * Validate deal close date
 * - Must be a valid date
 * - Should not be more than 5 years in the future (unrealistic)
 */
export function validateDealDate(date: unknown): ValidationResult<string | null> {
  if (date === null || date === undefined) {
    return {
      isValid: false,
      value: null,
      reason: "Close date is null or undefined",
    };
  }

  const dateObj =
    date instanceof Date ? date : new Date(date as string | number);
  if (isNaN(dateObj.getTime())) {
    return {
      isValid: false,
      value: null,
      reason: "Close date is not a valid date",
    };
  }

  const now = new Date();
  const fiveYearsFromNow = new Date();
  fiveYearsFromNow.setFullYear(fiveYearsFromNow.getFullYear() + 5);

  if (dateObj > fiveYearsFromNow) {
    return {
      isValid: false,
      value: null,
      reason: "Close date is more than 5 years in the future (unrealistic)",
    };
  }

  return {
    isValid: true,
    value: typeof date === "string" ? date : dateObj.toISOString(),
  };
}

/**
 * Validate estimated hours
 * - Must be a non-negative number
 * - Null/undefined is valid (hours are optional)
 */
export function validateEstimatedHours(
  hours: unknown
): ValidationResult<number | null> {
  if (hours === null || hours === undefined) {
    return {
      isValid: true,
      value: null,
    };
  }

  const numHours = Number(hours);
  if (isNaN(numHours)) {
    return {
      isValid: false,
      value: null,
      reason: "Estimated hours is not a valid number",
    };
  }

  if (numHours < 0) {
    return {
      isValid: false,
      value: null,
      reason: "Estimated hours cannot be negative",
    };
  }

  return {
    isValid: true,
    value: numHours,
  };
}

/**
 * Validate monthly recurring amount
 * - Must be a positive number (if provided)
 * - Null/undefined is valid (recurring amount is optional)
 */
export function validateMonthlyAmount(
  amount: unknown
): ValidationResult<number | null> {
  if (amount === null || amount === undefined) {
    return {
      isValid: true,
      value: null,
    };
  }

  const numAmount = Number(amount);
  if (isNaN(numAmount)) {
    return {
      isValid: false,
      value: null,
      reason: "Monthly amount is not a valid number",
    };
  }

  if (numAmount <= 0) {
    return {
      isValid: false,
      value: null,
      reason: "Monthly amount must be positive",
    };
  }

  return {
    isValid: true,
    value: numAmount,
  };
}
